import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setBoardPickerMode, setBoardPickerPendingCell } from "@/store/globalSlice"
import {
  boardPickerTitles,
  getBoardPickerEligibleOrders,
  getBoardPickerTargetKind,
  getOpponentsOnCell,
  playBoardPickerCard,
} from "@/utils/boardPickerHelpers"
import { actionCardTitles } from "@/utils/actionCardTitles"
import { allPieces, images } from "@/utils/data"
import { getPlayerAvatar } from "@/utils/playerAvatar"

// Stand-in face for a card with no artwork of its own (rent increase has
// none), so the panel still leads with something card-shaped.
const pickerIcons: Record<BoardPickerCardId, string> = {
  "forced-auction": "fa-gavel",
  "force-downgrade-property": "fa-hammer",
  "purchase-any-property-for-double-price": "fa-hand-holding-dollar",
  "rent-increase-of-property": "fa-arrow-trend-up",
  "swap-position-with-player": "fa-right-left",
}

// Takes over the middle of the board while a board-targeted action card
// (forced auction, demolish house, double-price purchase, rent increase, swap
// position) waits for its target. It sits in the centre rather than along the
// top edge on purpose: that is where the player is already looking between
// turns, and the board centre is dimmed in this mode, so a lit panel there
// reads as "the game is waiting on you" instead of as one more badge pinned to
// a corner.
//
// The panel leads with the card's own artwork, an arrow and an empty dashed
// slot, because the sentence underneath is the *second* thing anyone reads:
// the picture has to say "this card is looking for a cell to land on" on its
// own. Wording alone left players staring at a modal wondering what had gone
// wrong with their card.
//
// Rendered by Board at its top level, NOT inside #center — the brightness
// filter on that element would drag the panel down with everything else it
// dims.
const BoardPickerBanner: React.FC = () => {
  const dispatch = useAppDispatch()
  const boardPickerMode = useAppSelector(
    (state) => state.global.boardPickerMode
  )
  const myGame = useAppSelector((state) => state.global.myGame)

  if (!boardPickerMode) return null

  const { cardId, playerOrder, pendingCellOrder } = boardPickerMode
  const targetKind = getBoardPickerTargetKind(cardId)
  const cardArt: string | undefined = images.cards[cardId]

  // Second step of the swap pick: the tile that was clicked holds more than
  // one opponent, so the panel turns into a list of exactly those opponents.
  const crowdedTileOpponents =
    myGame && pendingCellOrder !== null && pendingCellOrder !== undefined
      ? getOpponentsOnCell(myGame, pendingCellOrder, playerOrder)
      : []
  const isChoosingOpponent = crowdedTileOpponents.length > 1

  // How many tiles are actually lit up out there. Worth spelling out: it turns
  // the glow into a countable set ("one of 6") instead of decoration, and it
  // is the only honest thing to show when a card has nothing left to hit.
  const eligibleCount = myGame
    ? getBoardPickerEligibleOrders(myGame, cardId, playerOrder).length
    : 0
  const hint =
    eligibleCount === 0
      ? "Nothing on the board can take this card right now"
      : eligibleCount === 1
      ? `Tap the glowing ${targetKind === "player" ? "tile" : "property"}`
      : `Tap one of the ${eligibleCount} glowing ${
          targetKind === "player" ? "tiles" : "properties"
        }`

  const handleChoosePlayer = (targetedPlayerOrder: PlayerOrder) => {
    if (!myGame?.id) return
    playBoardPickerCard(myGame.id, cardId, playerOrder, {
      kind: "player",
      targetedPlayerOrder,
    })
    dispatch(setBoardPickerMode(null))
  }

  return (
    <div
      data-testid="game.board-picker-banner"
      // Kept inside the board's centre square (the middle ~80% of the board) so
      // the panel never sits on top of the cells it is asking the player to
      // click — it would swallow their clicks at this z-index.
      className="board-picker-enter absolute top-1/2 left-1/2 z-[100] -translate-x-1/2 -translate-y-1/2 px-4 w-max max-w-[70%]"
    >
      {/* Glow behind the panel, breathing slowly — it separates the panel from
          the dimmed board without needing a heavier border. */}
      <span
        aria-hidden
        className="board-picker-halo pointer-events-none absolute -inset-6 rounded-[2rem]"
      />
      <div className="relative flex flex-col items-center gap-2.5 rounded-2xl border border-cyan-300/40 bg-gradient-to-b from-[#0b3b48]/95 to-[#04202a]/95 px-6 py-5 shadow-[0_18px_50px_rgba(0,0,0,0.6)] backdrop-blur-md sm:gap-1.5 sm:px-4 sm:py-3">
        <span className="font-poppins text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">
          {actionCardTitles[cardId]}
        </span>

        {isChoosingOpponent ? (
          // Tokens sharing a tile is common (jail especially), and the
          // stacked pieces on the board are too small to aim at individually,
          // so the choice is finished here rather than back on the board.
          <>
            <h2 className="text-center font-poppins text-lg font-bold leading-snug text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] sm:text-base">
              Several players on that tile — pick one
            </h2>
            <div className="flex w-full flex-col gap-2 sm:gap-1.5">
              {crowdedTileOpponents.map((opponent) => {
                const user = myGame?.players.find(
                  (p) => p.email === opponent.id
                )
                const piece = allPieces.find((p) => p.id === opponent.pieceId)
                return (
                  <button
                    key={opponent.order}
                    type="button"
                    data-testid={`game.board-picker-player-${opponent.order}`}
                    onClick={() => handleChoosePlayer(opponent.order)}
                    className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/5 px-3 py-2 text-left transition-colors hover:border-cyan-300/60 hover:bg-cyan-400/10"
                  >
                    <span className="relative shrink-0">
                      <img
                        className="h-10 w-10 rounded-full object-cover sm:h-8 sm:w-8"
                        src={
                          user?.profileImage
                            ? encodeURI(user.profileImage)
                            : getPlayerAvatar(opponent, user)
                        }
                      />
                      {piece?.imgSrc && (
                        <img
                          className="absolute -bottom-1 -right-1 h-5 w-5 drop-shadow-[0_1px_2px_black] sm:h-4 sm:w-4"
                          src={piece.imgSrc}
                        />
                      )}
                    </span>
                    <span className="font-poppins text-base font-semibold text-white sm:text-sm">
                      {user?.username}
                    </span>
                  </button>
                )
              })}
            </div>
          </>
        ) : (
          <>
            {/* card → empty slot: the whole point of the panel, said as a
                picture. The card is the real artwork the player just selected,
                tilted and floating so it reads as held rather than filed. */}
            <div className="flex items-center gap-3 py-1 sm:gap-2 sm:py-0">
              <div className="board-picker-card relative h-[4.4rem] w-[6.6rem] shrink-0 overflow-hidden rounded-lg border border-cyan-100/30 bg-[#fbe1a6] sm:h-[3.2rem] sm:w-[4.8rem]">
                {cardArt ? (
                  <img
                    src={cardArt}
                    alt={actionCardTitles[cardId]}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  // Not every card has artwork yet (rent increase doesn't), and
                  // a missing image here would break the one thing this panel
                  // is built around — so the fallback is still card-shaped.
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gradient-to-b from-[#fbe1a6] to-[#e8bf74] px-1">
                    <i
                      className={`fa-solid ${pickerIcons[cardId]} text-lg text-[#7c4a12] sm:text-sm`}
                    ></i>
                    <span className="text-center font-poppins text-[0.55rem] font-bold uppercase leading-tight text-[#7c4a12]">
                      {actionCardTitles[cardId]}
                    </span>
                  </div>
                )}
              </div>

              <span
                aria-hidden
                className="board-picker-trail flex items-center gap-0.5 text-cyan-300"
              >
                <i className="fa-solid fa-chevron-right text-xs"></i>
                <i className="fa-solid fa-chevron-right text-xs"></i>
                <i className="fa-solid fa-chevron-right text-xs"></i>
              </span>

              {/* The slot the card is waiting to be dropped into: dashed and
                  empty, pulsing on the same beat as the lit cells outside. */}
              <span className="board-picker-target flex h-[3.6rem] w-[3.6rem] shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-cyan-300/60 bg-cyan-400/5 sm:h-[2.7rem] sm:w-[2.7rem]">
                <i
                  className={`fa-solid ${
                    targetKind === "player"
                      ? "fa-user-group"
                      : "fa-location-crosshairs"
                  } text-xl text-cyan-200 sm:text-base`}
                ></i>
              </span>
            </div>

            <h2 className="text-center font-poppins text-lg font-bold leading-snug text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] sm:text-base">
              {boardPickerTitles[cardId]}
            </h2>

            {/* The panel names the action; this line names the gesture, since
                clicking the board itself is the part that is not obvious. */}
            <p
              className={`flex items-center gap-2 rounded-full border px-3 py-1 font-poppins text-xs sm:text-[0.7rem] ${
                eligibleCount === 0
                  ? "border-amber-300/40 bg-amber-400/10 text-amber-200/90"
                  : "border-cyan-300/25 bg-cyan-400/10 text-cyan-100/80"
              }`}
            >
              <i
                className={`fa-solid ${
                  eligibleCount === 0
                    ? "fa-triangle-exclamation"
                    : "fa-hand-pointer"
                }`}
              ></i>
              {hint}
            </p>
          </>
        )}

        {isChoosingOpponent ? (
          <button
            data-testid="game.board-picker-back"
            onClick={() => dispatch(setBoardPickerPendingCell(null))}
            className="mt-1 flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 font-poppins text-sm text-white/70 transition-colors hover:bg-white/20 hover:text-white sm:mt-0 sm:py-1 sm:text-xs"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i>
            Pick another tile
          </button>
        ) : (
          <button
            data-testid="game.board-picker-cancel"
            onClick={() => dispatch(setBoardPickerMode(null))}
            className="mt-1 flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 font-poppins text-sm text-white/70 transition-colors hover:bg-white/20 hover:text-white sm:mt-0 sm:py-1 sm:text-xs"
          >
            <i className="fa-solid fa-xmark text-xs"></i>
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}

export default BoardPickerBanner
